/**
 * PhoneManagementModal - Modal for managing Twilio phone numbers
 * Two tabs: "My Numbers" (list/release) and "Get New Number" (search/purchase)
 * Supports stacked AddBalanceModal for insufficient balance during purchase
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Phone, Search, Loader2, AlertCircle, AlertTriangle, CheckCircle, Trash2, MapPin, RefreshCw } from 'lucide-react';
import { AddBalanceModal } from './AddBalanceModal';

// ============================================================================
// Types
// ============================================================================

interface PhoneLine {
  id: string;
  phone_number: string;
  friendly_name: string | null;
  capabilities: { voice: boolean; sms: boolean; mms: boolean } | null;
  status: string;
  monthly_charge: number;
  next_bill_date: string | null;
  provisioned_date: string | null;
  messaging_service_added: boolean;
  purchased_in_app: boolean;
  twilio_phone_sid: string | null;
}

interface AvailableNumber {
  phone_number: string;
  friendly_name: string;
  locality: string | null;
  region: string | null;
  capabilities: { voice: boolean; sms: boolean; mms: boolean };
}

interface PhoneManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  currentBalance: number;
  isInternal?: boolean;
  onPhonePurchased: (newBalance: number) => void;
  onPhoneReleased: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

type ActiveTab = 'my-numbers' | 'get-new';

function formatPhoneNumber(e164: string): string {
  // +17025551234 → (702) 555-1234
  const digits = e164.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    const area = digits.substring(1, 4);
    const prefix = digits.substring(4, 7);
    const line = digits.substring(7, 11);
    return `(${area}) ${prefix}-${line}`;
  }
  return e164;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ============================================================================
// Component
// ============================================================================

export function PhoneManagementModal({
  isOpen,
  onClose,
  locationId,
  currentBalance,
  isInternal,
  onPhonePurchased,
  onPhoneReleased,
}: PhoneManagementModalProps) {
  // Tab state
  const [activeTab, setActiveTab] = useState<ActiveTab>('my-numbers');

  // Phone lines state
  const [phoneLines, setPhoneLines] = useState<PhoneLine[]>([]);
  const [phoneLinesLoading, setPhoneLinesLoading] = useState(false);
  const [phoneLinesError, setPhoneLinesError] = useState<string | null>(null);

  // Search state
  const [areaCode, setAreaCode] = useState('');
  const [availableNumbers, setAvailableNumbers] = useState<AvailableNumber[]>([]);
  const [customerCharge, setCustomerCharge] = useState<number | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Purchase state
  const [purchasingNumber, setPurchasingNumber] = useState<string | null>(null);
  const [confirmingPurchase, setConfirmingPurchase] = useState<string | null>(null);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState<string | null>(null);
  const purchaseRef = useRef(false);

  // Release state
  const [confirmingRelease, setConfirmingRelease] = useState<string | null>(null);
  const [releasingId, setReleasingId] = useState<string | null>(null);
  const [releaseError, setReleaseError] = useState<string | null>(null);

  // Stacked Add Balance modal
  const [showAddBalanceModal, setShowAddBalanceModal] = useState(false);
  const [liveBalance, setLiveBalance] = useState(currentBalance);

  // Update live balance when prop changes
  useEffect(() => {
    setLiveBalance(currentBalance);
  }, [currentBalance]);

  // ============================================================================
  // Data Fetching
  // ============================================================================

  const fetchPhoneLines = useCallback(async () => {
    setPhoneLinesLoading(true);
    setPhoneLinesError(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/phones`,
        { credentials: 'include' }
      );
      const data = await response.json() as {
        success: boolean;
        phone_lines?: PhoneLine[];
        error?: string;
      };
      if (data.success) {
        setPhoneLines(data.phone_lines || []);
      } else {
        setPhoneLinesError(data.error || 'Failed to load phone lines');
      }
    } catch {
      setPhoneLinesError('Failed to load phone lines');
    } finally {
      setPhoneLinesLoading(false);
    }
  }, [locationId]);

  const fetchCost = useCallback(async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/phones/cost`,
        { credentials: 'include' }
      );
      const data = await response.json() as {
        success: boolean;
        customer_charge?: number;
      };
      if (data.success && data.customer_charge) {
        setCustomerCharge(data.customer_charge);
      }
    } catch {
      // Non-critical, will be fetched with search results too
    }
  }, [locationId]);

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPhoneLines();
      fetchCost();
      // Reset states
      setActiveTab('my-numbers');
      setAreaCode('');
      setAvailableNumbers([]);
      setHasSearched(false);
      setSearchError(null);
      setPurchaseError(null);
      setPurchaseSuccess(null);
      setConfirmingPurchase(null);
      setConfirmingRelease(null);
      setReleaseError(null);
    }
  }, [isOpen, fetchPhoneLines, fetchCost]);

  // ============================================================================
  // Search
  // ============================================================================

  const handleSearch = async () => {
    if (!/^\d{3}$/.test(areaCode)) return;

    setSearching(true);
    setSearchError(null);
    setAvailableNumbers([]);
    setHasSearched(true);
    setPurchaseError(null);
    setPurchaseSuccess(null);
    setConfirmingPurchase(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/phones/search?area_code=${areaCode}`,
        { credentials: 'include' }
      );
      const data = await response.json() as {
        success: boolean;
        available_numbers?: AvailableNumber[];
        customer_charge?: number;
        error?: string;
      };

      if (data.success) {
        setAvailableNumbers(data.available_numbers || []);
        if (data.customer_charge) {
          setCustomerCharge(data.customer_charge);
        }
      } else {
        setSearchError(data.error || 'Search failed');
      }
    } catch {
      setSearchError('Failed to search for numbers. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  // ============================================================================
  // Purchase
  // ============================================================================

  const handlePurchaseClick = (phoneNumber: string) => {
    setPurchaseError(null);
    setPurchaseSuccess(null);
    setConfirmingPurchase(phoneNumber);
  };

  const handlePurchaseConfirm = async () => {
    if (!confirmingPurchase || purchaseRef.current) return;
    purchaseRef.current = true;

    setPurchasingNumber(confirmingPurchase);
    setPurchaseError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/phones/purchase`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone_number: confirmingPurchase }),
        }
      );

      const data = await response.json() as {
        success: boolean;
        phone_line?: { id: string; phone_number: string; monthly_charge: number; next_bill_date: string };
        new_balance?: number;
        error?: string;
        wallet_balance?: number;
        required_amount?: number;
      };

      if (data.success) {
        setPurchaseSuccess(data.phone_line?.phone_number || confirmingPurchase);
        setConfirmingPurchase(null);

        // Update balance and notify parent
        const newBalance = data.new_balance ?? liveBalance;
        setLiveBalance(newBalance);
        onPhonePurchased(newBalance);

        // Remove purchased number from search results
        setAvailableNumbers((prev) => prev.filter((n) => n.phone_number !== confirmingPurchase));

        // Refresh phone lines list
        await fetchPhoneLines();

        // Auto-switch to My Numbers tab after short delay
        setTimeout(() => {
          setActiveTab('my-numbers');
          setPurchaseSuccess(null);
        }, 2000);
      } else {
        if (data.error === 'Insufficient wallet balance') {
          setPurchaseError(
            `Insufficient balance. You need ${formatCurrency(data.required_amount || 0)} but have ${formatCurrency(data.wallet_balance || 0)}.`
          );
        } else {
          setPurchaseError(data.error || 'Purchase failed');
        }
      }
    } catch {
      setPurchaseError('An error occurred during purchase. Please try again.');
    } finally {
      setPurchasingNumber(null);
      purchaseRef.current = false;
    }
  };

  // ============================================================================
  // Release
  // ============================================================================

  const handleReleaseConfirm = async (phoneLineId: string) => {
    setReleasingId(phoneLineId);
    setReleaseError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/phones/${phoneLineId}/release`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        setConfirmingRelease(null);
        setPhoneLines((prev) => prev.filter((p) => p.id !== phoneLineId));
        onPhoneReleased();
      } else {
        setReleaseError(data.error || 'Release failed');
      }
    } catch {
      setReleaseError('An error occurred while releasing the number.');
    } finally {
      setReleasingId(null);
    }
  };

  // ============================================================================
  // Add Balance callback
  // ============================================================================

  const handleBalanceAdded = (newBalance: number) => {
    setLiveBalance(newBalance);
    setShowAddBalanceModal(false);
    // Clear the insufficient balance error so user can retry
    setPurchaseError(null);
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (!isOpen) return null;

  const insufficientBalance = customerCharge != null && !isInternal && liveBalance < customerCharge;

  return (
    <>
      <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${showAddBalanceModal ? 'pointer-events-none' : ''}`}>
        {/* Backdrop */}
        <div
          className={`fixed inset-0 bg-black/50 transition-opacity ${showAddBalanceModal ? 'bg-black/30' : ''}`}
          onClick={showAddBalanceModal ? undefined : onClose}
        />

        {/* Modal */}
        <div className={`relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col ${showAddBalanceModal ? 'opacity-50' : ''}`}>
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Manage Phone Lines</h2>
            </div>
            <button
              onClick={onClose}
              disabled={showAddBalanceModal || !!purchasingNumber || !!releasingId}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex-shrink-0 flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('my-numbers')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'my-numbers'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              My Numbers ({phoneLines.length})
            </button>
            <button
              onClick={() => setActiveTab('get-new')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'get-new'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Get New Number
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* ============ TAB: MY NUMBERS ============ */}
            {activeTab === 'my-numbers' && (
              <>
                {phoneLinesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                    <span className="ml-2 text-sm text-gray-500">Loading phone lines...</span>
                  </div>
                ) : phoneLinesError ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                    <p className="text-sm text-red-600">{phoneLinesError}</p>
                    <button
                      onClick={fetchPhoneLines}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 mx-auto"
                    >
                      <RefreshCw className="w-3 h-3" /> Retry
                    </button>
                  </div>
                ) : phoneLines.length === 0 ? (
                  <div className="text-center py-12">
                    <Phone className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No active phone lines</p>
                    <button
                      onClick={() => setActiveTab('get-new')}
                      className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Get your first number
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {phoneLines.map((phone) => (
                      <div
                        key={phone.id}
                        className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatPhoneNumber(phone.phone_number)}
                            </p>
                            {phone.friendly_name && (
                              <p className="text-xs text-gray-500">{phone.friendly_name}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-700">
                              {formatCurrency(phone.monthly_charge)}/mo
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>Next bill: {formatDate(phone.next_bill_date)}</span>
                            {phone.messaging_service_added && (
                              <span className="text-green-600 flex items-center gap-0.5">
                                <CheckCircle className="w-3 h-3" /> A2P
                              </span>
                            )}
                          </div>

                          {/* Release button */}
                          {confirmingRelease === phone.id ? (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleReleaseConfirm(phone.id)}
                                disabled={!!releasingId}
                                className="px-2 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                              >
                                {releasingId === phone.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : null}
                                Confirm
                              </button>
                              <button
                                onClick={() => { setConfirmingRelease(null); setReleaseError(null); }}
                                disabled={!!releasingId}
                                className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setConfirmingRelease(phone.id); setReleaseError(null); }}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                              title="Release number"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {/* Release confirmation warning */}
                        {confirmingRelease === phone.id && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                            <AlertTriangle className="w-3 h-3 inline mr-1" />
                            This will immediately release {formatPhoneNumber(phone.phone_number)} from your account. No refund will be issued for the remaining billing period.
                          </div>
                        )}

                        {/* Release error */}
                        {confirmingRelease === phone.id && releaseError && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
                            <AlertCircle className="w-3 h-3 inline mr-1" />
                            {releaseError}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ============ TAB: GET NEW NUMBER ============ */}
            {activeTab === 'get-new' && (
              <>
                {/* Cost display */}
                {customerCharge != null && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                    <span className="text-gray-700">Monthly cost per line: </span>
                    <span className="font-semibold text-blue-700">{formatCurrency(customerCharge)}/mo</span>
                    {!isInternal && (
                      <span className="text-gray-500 ml-2">
                        (Balance: {formatCurrency(liveBalance)})
                      </span>
                    )}
                  </div>
                )}

                {/* GHL sync notice */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>
                    After purchasing, go to <strong>Settings &gt; Phone</strong> and click <strong>Add</strong> on the new number to complete setup.
                  </span>
                </div>

                {/* Area Code Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search by Area Code
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={areaCode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 3);
                        setAreaCode(val);
                      }}
                      placeholder="e.g., 702"
                      maxLength={3}
                      disabled={searching || !!purchasingNumber}
                      onKeyDown={(e) => { if (e.key === 'Enter' && areaCode.length === 3) handleSearch(); }}
                      className="flex-1 px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    />
                    <button
                      onClick={handleSearch}
                      disabled={areaCode.length !== 3 || searching || !!purchasingNumber}
                      className="px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      {searching ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                      Search
                    </button>
                  </div>
                </div>

                {/* Purchase Success */}
                {purchaseSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-700 font-medium">
                      {formatPhoneNumber(purchaseSuccess)} purchased successfully!
                    </p>
                  </div>
                )}

                {/* Purchase Error */}
                {purchaseError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-red-700">{purchaseError}</p>
                        {purchaseError.includes('Insufficient') && (
                          <button
                            onClick={() => setShowAddBalanceModal(true)}
                            className="mt-2 px-3 py-1.5 bg-orange-600 text-white text-sm font-medium rounded hover:bg-orange-700 transition-colors"
                          >
                            Add Balance Now
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Search Error */}
                {searchError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-600">{searchError}</p>
                  </div>
                )}

                {/* Search Results */}
                {hasSearched && !searching && !searchError && (
                  <>
                    {availableNumbers.length === 0 ? (
                      <div className="text-center py-8">
                        <Phone className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No numbers available for area code {areaCode}</p>
                        <p className="text-xs text-gray-400 mt-1">Try a different area code</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {availableNumbers.map((num) => (
                          <div
                            key={num.phone_number}
                            className="border border-gray-200 rounded-lg p-3 hover:border-blue-200 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-semibold text-gray-900">
                                  {formatPhoneNumber(num.phone_number)}
                                </p>
                                {(num.locality || num.region) && (
                                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                    <MapPin className="w-3 h-3" />
                                    {[num.locality, num.region].filter(Boolean).join(', ')}
                                  </p>
                                )}
                              </div>

                              {/* Purchase button / confirmation */}
                              {confirmingPurchase === num.phone_number ? (
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={handlePurchaseConfirm}
                                    disabled={!!purchasingNumber || (insufficientBalance && !isInternal)}
                                    className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                                  >
                                    {purchasingNumber === num.phone_number ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : null}
                                    Buy
                                  </button>
                                  <button
                                    onClick={() => { setConfirmingPurchase(null); setPurchaseError(null); }}
                                    disabled={!!purchasingNumber}
                                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handlePurchaseClick(num.phone_number)}
                                  disabled={!!purchasingNumber}
                                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                >
                                  Purchase
                                </button>
                              )}
                            </div>

                            {/* Confirmation details */}
                            {confirmingPurchase === num.phone_number && (
                              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                                First month charge of {formatCurrency(customerCharge || 0)} will be deducted from your wallet.
                                {insufficientBalance && !isInternal && (
                                  <span className="block mt-1 text-orange-700">
                                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                                    Insufficient balance.{' '}
                                    <button
                                      onClick={() => setShowAddBalanceModal(true)}
                                      className="underline hover:no-underline font-medium"
                                    >
                                      Add funds first
                                    </button>
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}

                        {/* Search again button */}
                        <div className="pt-2 text-center">
                          <button
                            onClick={handleSearch}
                            disabled={searching || !!purchasingNumber}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 mx-auto"
                          >
                            <RefreshCw className="w-3 h-3" /> Show different numbers
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Loading spinner during search */}
                {searching && (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                    <span className="ml-2 text-sm text-gray-500">Searching available numbers...</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={showAddBalanceModal || !!purchasingNumber || !!releasingId}
              className="w-full py-2.5 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Stacked Add Balance Modal */}
      <AddBalanceModal
        isOpen={showAddBalanceModal}
        onClose={() => setShowAddBalanceModal(false)}
        locationId={locationId}
        currentBalance={liveBalance}
        onBalanceAdded={handleBalanceAdded}
      />
    </>
  );
}
